import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight, ChevronUp } from "lucide-react";
import {
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaPhoneAlt,
} from "react-icons/fa";
import logo from "../assets/images/logo/logo.png";

const COLORS = {
  gold: "#C8A27C",
  black: "#080C10",
  cardSlate: "rgba(13, 20, 28, 0.7)",
  border: "rgba(255, 255, 255, 0.08)",
};

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const currentYear = new Date().getFullYear();

  // Handle Scroll Visibility for Move-to-Top
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) setIsVisible(true);
      else setIsVisible(false);
    };
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <footer
        style={{ background: COLORS.black }}
        className="text-white pt-24 pb-12 relative overflow-hidden"
      >
        {/* SUBTLE BACKGROUND ACCENT */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* BENTO CTA SECTION */}
          <div
            className="backdrop-blur-md p-10 md:p-14 rounded-[2.5rem] border border-white/5 mb-24 flex flex-col md:flex-row items-center justify-between gap-8 group"
            style={{ background: COLORS.cardSlate }}
          >
            <div className="max-w-2xl text-center md:text-left">
              <span className="text-[10px] uppercase tracking-[0.5em] text-white/40 mb-4 block">
                Consultation
              </span>
              <h3 className="text-3xl md:text-5xl font-serif font-medium mb-6 leading-tight">
                Crafting Excellence in <br />
                <span
                  className="italic font-light"
                  style={{ color: COLORS.gold }}
                >
                  Legal Defense.
                </span>
              </h3>
              <p className="text-gray-400 font-light max-w-md">
                Strategizing complex litigation with a focus on integrity and
                precision.
              </p>
            </div>
            <Link
              to="/contact-us"
              className="relative overflow-hidden px-12 py-5 rounded-full font-bold uppercase tracking-widest text-[10px] transition-all duration-500 bg-white text-black hover:bg-[#C8A27C] hover:text-white"
            >
              <div className="flex items-center gap-2">
                <span>Inquire Now</span>
                <ArrowUpRight size={16} />
              </div>
            </Link>
          </div>

          {/* MAIN INFORMATION GRID */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-20">
            {/* BRAND COLUMN */}
            <div className="lg:col-span-4">
              <img
                src={logo}
                alt="Adv. Saurav Dagar"
                className="h-14 mb-8 brightness-0 invert opacity-80"
              />
              <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-sm">
                Dedicated to providing high-stakes litigation services across
                Civil, Criminal, and Matrimonial law within the Uttar Pradesh
                judicial circuit.
              </p>
              <div className="flex gap-3">
                <SocialIcon
                  href="https://www.facebook.com/your-page-name"
                  target="_blank"
                >
                  <FaFacebookF size={14} />
                </SocialIcon>

                <SocialIcon
                  href="https://www.instagram.com/your-username"
                  target="_blank"
                >
                  <FaInstagram size={14} />
                </SocialIcon>
              </div>
            </div>

            {/* NAV LINKS */}
            <div className="lg:col-span-2">
              <h4 className="text-[10px] uppercase tracking-[0.3em] font-bold mb-8 text-white/50">
                Firm
              </h4>
              <ul className="space-y-4">
                <FooterLink label="Home" href="/" />
                <FooterLink label="About" href="/about-us" />
                <FooterLink label="Insights" href="/blogs" />
                <FooterLink label="Services" href="/services" />
                <FooterLink label="Contact" href="/contact-us" />
              </ul>
            </div>

            {/* PRACTICE AREAS */}
            <div className="lg:col-span-3">
              <h4 className="text-[10px] uppercase tracking-[0.3em] font-bold mb-8 text-white/50">
                Expertise
              </h4>
              <ul className="space-y-4">
                <FooterLink
                  label="Civil Litigation"
                  href="/services/civil-cases"
                />
                <FooterLink
                  label="Criminal Defense"
                  href="/services/criminal-cases"
                />
                <FooterLink
                  label="Matrimonial Law"
                  href="/services/matrimonial-Cases"
                />
                <FooterLink
                  label="MACT Cases"
                  href="/services/accidental-cases"
                />
              </ul>
            </div>

            {/* CONTACT */}
            <div className="lg:col-span-3">
              <h4 className="text-[10px] uppercase tracking-[0.3em] font-bold mb-8 text-white/50">
                Location
              </h4>
              <div className="space-y-6">
                <ContactItem
                  icon={<MapPin size={18} />}
                  text="Chamber no. 469, civil court Compound, RDC, Sector 15, Sector 10, Raj Nagar, Ghaziabad, Uttar Pradesh 201002"
                />
                <ContactItem
                  icon={<Phone size={18} />}
                  text="+91 84472 20987"
                />
                <ContactItem
                  icon={<Mail size={18} />}
                  text="sauravdagar91@gmail.com"
                />
              </div>
            </div>
          </div>

          {/* BOTTOM BAR */}
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-white/20">
            <p>© {currentYear} Adv. Saurav Dagar</p>
            <p>
              Developed by
              <a
                href="https://www.digimarqtechnology.com/"
                className="text-white/40 hover:text-[#C8A27C] ml-1 transition-colors"
              >
                Developed & Designed by Digimarq Technology Pvt. Ltd.
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* FLOATING ACTION DOCK */}
      <div className="fixed bottom-8 right-8 z-[9999] flex flex-col gap-4">
        {/* Back to Top */}
        <button
          onClick={scrollToTop}
          className={`w-12 h-12 bg-white/5 backdrop-blur-xl border border-white/10 text-white rounded-full flex items-center justify-center transition-all duration-500 shadow-2xl ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0"
          } hover:bg-white hover:text-black`}
        >
          <ChevronUp size={20} />
        </button>

        {/* Call Icon */}
        <a
          href="tel:+918447220987"
          className="w-14 h-14 bg-[#C8A27C] text-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 active:scale-95 transition-all"
        >
          <FaPhoneAlt size={18} />
        </a>

        {/* WhatsApp Icon */}
        <a
          href="https://wa.me/918447220987"
          target="_blank"
          rel="noreferrer"
          className="w-14 h-14 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 active:scale-95 transition-all"
        >
          <FaWhatsapp size={26} />
        </a>
      </div>
    </>
  );
};

/* COMPONENTS */
const SocialIcon = ({ children, href }) => (
  <a
    href={href}
    className="w-10 h-10 flex items-center justify-center rounded-full border border-white/5 bg-white/5 hover:bg-[#C8A27C] hover:border-[#C8A27C] transition-all duration-500"
  >
    {children}
  </a>
);

const FooterLink = ({ label, href }) => (
  <li>
    <Link
      to={href}
      className="text-sm text-gray-500 hover:text-white transition-all duration-300 flex items-center group"
    >
      <span className="w-0 group-hover:w-3 h-[1px] bg-[#C8A27C] mr-0 group-hover:mr-2 transition-all"></span>
      {label}
    </Link>
  </li>
);

const ContactItem = ({ icon, text }) => (
  <div className="flex gap-4 items-start group">
    <div className="text-gray-600 group-hover:text-[#C8A27C] transition-colors">
      {icon}
    </div>
    <span className="text-sm text-gray-500 font-light">{text}</span>
  </div>
);

export default Footer;
